import { Code, ConnectError } from "@connectrpc/connect";

import {
  ErrorCode,
  ErrorDetailSchema,
  type ErrorDetail,
  type FieldViolation,
} from "@/gen/bmi/v1/batch_message_pb";

/**
 * Error plumbing for the admin UI.
 *
 * The backend answers with Connect errors that carry a `bmi.v1.ErrorDetail`
 * detail: a coarse `code` plus zero or more `field_violations` whose `field` is
 * a dotted path rooted at the request message (`send_policy.msgs_per_minute`,
 * `messages[3].destination.id`). Screens surface the violations next to the
 * offending control where one exists, and fall back to the plain message.
 */

/** Every Connect error handling path funnels through this. */
export function asConnectError(reason: unknown): ConnectError {
  return ConnectError.from(reason);
}

export function errorDetail(reason: unknown): ErrorDetail | undefined {
  if (reason === null || reason === undefined) return undefined;
  return asConnectError(reason).findDetails(ErrorDetailSchema)[0];
}

/** Every field violation attached to the error, in server order. */
export function fieldViolations(reason: unknown): FieldViolation[] {
  return errorDetail(reason)?.fieldViolations ?? [];
}

/**
 * Groups violations by their dotted path so a form can look one up per control.
 * A path may legitimately repeat; the descriptions are joined.
 */
export function violationsByField(reason: unknown): Map<string, string> {
  const out = new Map<string, string>();
  for (const v of fieldViolations(reason)) {
    const existing = out.get(v.field);
    out.set(v.field, existing ? `${existing} ${v.description}` : v.description);
  }
  return out;
}

/**
 * Looks a violation up by exact path, then by trailing path segment, so a
 * screen can ask for `msgs_per_minute` and still match
 * `send_policy.msgs_per_minute`.
 */
export function violationFor(reason: unknown, field: string): string | undefined {
  const violations = fieldViolations(reason);
  const exact = violations.find((v) => v.field === field);
  if (exact) return exact.description;
  const suffixed = violations.find((v) => v.field.endsWith(`.${field}`));
  return suffixed?.description;
}

/** Violations that no control on the current form claims, so nothing is lost. */
export function unclaimedViolations(reason: unknown, claimed: readonly string[]): FieldViolation[] {
  return fieldViolations(reason).filter(
    (v) => !claimed.some((field) => v.field === field || v.field.endsWith(`.${field}`)),
  );
}

const ERROR_CODE_LABEL: Record<ErrorCode, string> = {
  [ErrorCode.UNSPECIFIED]: "Error",
  [ErrorCode.INVALID_ARGUMENT]: "Invalid argument",
  [ErrorCode.UNAUTHENTICATED]: "Not signed in",
  [ErrorCode.PERMISSION_DENIED]: "Permission denied",
  [ErrorCode.NOT_FOUND]: "Not found",
  [ErrorCode.ALREADY_EXISTS]: "Already exists",
  [ErrorCode.FAILED_PRECONDITION]: "Failed precondition",
  [ErrorCode.RESOURCE_EXHAUSTED]: "Resource exhausted",
  [ErrorCode.UNIMPLEMENTED]: "Not implemented",
  [ErrorCode.UNAVAILABLE]: "Service unavailable",
  [ErrorCode.INTERNAL]: "Internal error",
};

/** Short heading for an alert; the message itself goes in the body. */
export function errorTitle(reason: unknown, fallback = "Request failed"): string {
  const detail = errorDetail(reason);
  if (detail && detail.code !== ErrorCode.UNSPECIFIED) return ERROR_CODE_LABEL[detail.code];
  if (reason === null || reason === undefined) return fallback;
  switch (asConnectError(reason).code) {
    case Code.Unauthenticated:
      return "Not signed in";
    case Code.PermissionDenied:
      return "Permission denied";
    case Code.NotFound:
      return "Not found";
    case Code.Unavailable:
      return "Service unavailable";
    case Code.DeadlineExceeded:
      return "Timed out";
    default:
      return fallback;
  }
}

/**
 * Human-readable text for an operator. Never returns an empty string: an error
 * that is invisible in the UI is worse than an ugly one.
 */
export function errorMessage(reason: unknown, fallback = "The service rejected the request."): string {
  if (reason === null || reason === undefined) return fallback;
  const err = asConnectError(reason);
  const message = err.rawMessage.trim();
  if (message.length > 0) return message;
  const violations = fieldViolations(err);
  if (violations.length > 0) {
    return violations.map((v) => `${v.field}: ${v.description}`).join("; ");
  }
  return fallback;
}

export function isUnauthenticated(reason: unknown): boolean {
  if (reason === null || reason === undefined) return false;
  const err = asConnectError(reason);
  if (err.code === Code.Unauthenticated) return true;
  return errorDetail(err)?.code === ErrorCode.UNAUTHENTICATED;
}

export function isNotFound(reason: unknown): boolean {
  if (reason === null || reason === undefined) return false;
  const err = asConnectError(reason);
  if (err.code === Code.NotFound) return true;
  return errorDetail(err)?.code === ErrorCode.NOT_FOUND;
}

/** True when the call was cancelled by us (dialog closed, component unmounted). */
export function isCanceled(reason: unknown): boolean {
  if (reason === null || reason === undefined) return false;
  return asConnectError(reason).code === Code.Canceled;
}
